"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * EPIC-041 STORY-2102 — embedded Tenor v2 GIF picker for the unified
 * post composer.
 *
 * Tenor's free tier requires a Google Cloud API key (no billing on
 * the key itself per founder spec, but the key has to exist). Frontend
 * reads it from `NEXT_PUBLIC_TENOR_API_KEY`. When unset, the picker
 * renders a placeholder explaining the env var so a future founder
 * provisioning step "just works" — no code change needed.
 *
 * The picker hits two endpoints:
 *   GET https://tenor.googleapis.com/v2/featured?key=…&limit=20
 *   GET https://tenor.googleapis.com/v2/search?key=…&q=…&limit=20
 *
 * Selecting a GIF returns its `media_formats.gif.url` (full size; the
 * feed render component already handles aspect ratio + lazy loading
 * the same way as user-uploaded photos).
 */

interface TenorMediaFormat {
  url: string;
  dims: [number, number];
}

interface TenorResult {
  id: string;
  media_formats: {
    gif?: TenorMediaFormat;
    tinygif?: TenorMediaFormat;
    mediumgif?: TenorMediaFormat;
  };
  content_description?: string;
}

interface TenorResponse {
  results: TenorResult[];
}

const TENOR_BASE = "https://tenor.googleapis.com/v2";

export function TenorGifPicker(props: {
  open: boolean;
  onClose: () => void;
  onPick: (gifUrl: string) => void;
}) {
  const apiKey =
    typeof process !== "undefined"
      ? process.env.NEXT_PUBLIC_TENOR_API_KEY ?? null
      : null;
  const [q, setQ] = useState("");
  const [results, setResults] = useState<TenorResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!props.open) return;
    if (!apiKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const url = q.trim()
          ? `${TENOR_BASE}/search?key=${encodeURIComponent(
              apiKey,
            )}&q=${encodeURIComponent(q.trim())}&limit=20&media_filter=gif,tinygif,mediumgif`
          : `${TENOR_BASE}/featured?key=${encodeURIComponent(
              apiKey,
            )}&limit=20&media_filter=gif,tinygif,mediumgif`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Tenor ${res.status}`);
        const data: TenorResponse = await res.json();
        setResults(data.results ?? []);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load GIFs.",
        );
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, props.open, apiKey]);

  // Esc to close.
  useEffect(() => {
    if (!props.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [props.open, props]);

  if (!props.open) return null;
  if (typeof document === "undefined") return null;

  // Same Portal pattern as PostComposer: backdrop-filter on the
  // SiteHeader makes `position: fixed` re-root to the header box
  // instead of the viewport. createPortal escapes to <body>.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Pick a GIF"
    >
      <div className="flex w-full max-w-lg flex-col bg-[var(--color-surface)] shadow-2xl sm:mx-4 sm:my-4 sm:max-h-[calc(100dvh-2rem)] sm:rounded-lg sm:border sm:border-[var(--color-border)]">
        <header className="flex flex-shrink-0 items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">
            Pick a GIF
          </h3>
          <button
            type="button"
            className="text-[var(--color-text3)] hover:text-[var(--color-text)] text-2xl leading-none p-1 -mr-1"
            onClick={props.onClose}
            aria-label="Close GIF picker"
          >
            ×
          </button>
        </header>

        {!apiKey ? (
          <div className="p-5 text-sm text-[var(--color-text2)]">
            <p className="mb-2 font-medium text-[var(--color-text)]">
              GIF picker not yet configured.
            </p>
            <p className="text-xs text-[var(--color-text3)]">
              Tenor v2 requires a Google Cloud API key. Set{" "}
              <code
                className="rounded px-1 py-0.5 text-[11px]"
                style={{
                  background: "var(--color-bg)",
                  border: "1px solid var(--color-border)",
                }}
              >
                NEXT_PUBLIC_TENOR_API_KEY
              </code>{" "}
              in the web app&apos;s environment to enable this picker.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Tenor…"
              autoFocus
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text3)] focus:outline-none focus:border-[var(--color-border2)]"
            />
            {error ? (
              <div
                className="rounded px-3 py-2 text-xs"
                style={{
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor:
                    "color-mix(in srgb, var(--color-no) 40%, transparent)",
                  background:
                    "color-mix(in srgb, var(--color-no) 10%, transparent)",
                  color: "var(--color-status-negative)",
                }}
              >
                {error}
              </div>
            ) : null}
            {loading ? (
              <div className="text-xs text-[var(--color-text3)]">
                Loading…
              </div>
            ) : results.length === 0 ? (
              <div className="text-xs text-[var(--color-text3)]">
                No GIFs found.
              </div>
            ) : (
              <ul className="grid grid-cols-3 gap-2">
                {results.map((r) => {
                  const url =
                    r.media_formats.gif?.url ??
                    r.media_formats.mediumgif?.url ??
                    r.media_formats.tinygif?.url ??
                    null;
                  const preview =
                    r.media_formats.tinygif?.url ??
                    r.media_formats.mediumgif?.url ??
                    r.media_formats.gif?.url ??
                    null;
                  if (!url || !preview) return null;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => {
                          props.onPick(url);
                          props.onClose();
                        }}
                        className="block w-full overflow-hidden rounded border border-[var(--color-border)] hover:border-[var(--color-border2)] transition-colors"
                        title={r.content_description ?? "GIF"}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={preview}
                          alt={r.content_description ?? "GIF"}
                          loading="lazy"
                          className="w-full h-auto"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-[10px] text-[var(--color-text3)]">
              GIFs via Tenor.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
